import * as React from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { Ziggy } from "../../Ziggy";
import { EASE_OUT } from "../../../lib/motion";
import { celebrateStepComplete } from "../../../lib/roadmapCelebration";
import type { ZiggyAge } from "../../../lib/ziggy";
import { useLearnLocale } from "../../../lib/i18n/useLearnLocale";

const GRAFFITI = ["NICE!", "STEP UP!", "YOU GOT IT!", "ONWARD!"] as const;

/**
 * Full-screen step qualification celebration — Ziggy, graffiti, confetti, fanfare.
 */
export function StepCelebrationOverlay({
  open,
  ziggyAge = "grown",
  onDone,
}: {
  open: boolean;
  ziggyAge?: ZiggyAge;
  onDone?: () => void;
}) {
  const reduce = useReducedMotion();
  const { strings } = useLearnLocale();
  const [graffiti, setGraffiti] = React.useState<(typeof GRAFFITI)[number]>(GRAFFITI[0]);

  React.useEffect(() => {
    if (!open) return;
    setGraffiti(GRAFFITI[Math.floor(Math.random() * GRAFFITI.length)] ?? GRAFFITI[0]);
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    celebrateStepComplete(Boolean(reduce));
    const t = window.setTimeout(() => onDone?.(), reduce ? 400 : 2400);
    return () => window.clearTimeout(t);
  }, [open, reduce, onDone]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          role="status"
          aria-live="polite"
          className="bg-background/50 pointer-events-none fixed inset-0 z-50 flex items-center justify-center backdrop-blur-[3px]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduce ? 0 : 0.22 }}
        >
          <motion.div
            initial={{ scale: reduce ? 1 : 0.9, opacity: 0, rotate: reduce ? 0 : -2 }}
            animate={{ scale: 1, opacity: 1, rotate: 0 }}
            exit={{ scale: reduce ? 1 : 0.96, opacity: 0 }}
            transition={{ duration: reduce ? 0 : 0.38, ease: EASE_OUT }}
            className="border-primary/25 from-primary/8 via-background to-background relative flex flex-col items-center gap-3 overflow-hidden rounded-3xl border bg-gradient-to-br px-10 py-7 shadow-2xl"
          >
            <span
              aria-hidden
              className="text-primary/20 font-display pointer-events-none absolute -left-2 top-2 -rotate-12 text-2xl font-black uppercase tracking-tight"
            >
              {graffiti}
            </span>
            <span
              aria-hidden
              className="text-joy-green/25 font-display pointer-events-none absolute -right-1 bottom-3 rotate-6 text-lg font-bold uppercase"
            >
              ✓
            </span>

            <motion.div
              animate={reduce ? undefined : { y: [0, -6, 0] }}
              transition={{ duration: 1.4, repeat: reduce ? 0 : 2, ease: "easeInOut" }}
            >
              <Ziggy age={ziggyAge} pose="excited" className="size-24" float={!reduce} />
            </motion.div>

            <p className="font-display text-xl font-semibold">{strings.stepCompleteTitle}</p>
            <p className="text-muted-foreground text-sm">{strings.stepCompleteBody}</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
